import React, { createContext, useContext, useCallback } from 'react';
import { useNotificationStore, Notification } from '../stores/notificationStore';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  info: (message: string, title?: string, link?: string) => void;
  success: (message: string, title?: string, link?: string) => void;
  warning: (message: string, title?: string, link?: string) => void;
  errorNotification: (message: string, title?: string, link?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const store = useNotificationStore();

  const notify = useCallback((type: Notification['type'], message: string, title: string = '', link?: string) => {
    store.addNotification({
      title,
      message,
      type,
      link
    });
  }, [store]);

  const contextValue: NotificationContextType = {
    notifications: store.notifications,
    unreadCount: store.unreadCount,
    addNotification: store.addNotification,
    markAsRead: store.markAsRead,
    markAllAsRead: store.markAllAsRead,
    removeNotification: store.removeNotification,
    clearAll: store.clearAll,
    info: (message, title, link) => notify('info', message, title, link),
    success: (message, title, link) => notify('success', message, title, link),
    warning: (message, title, link) => notify('warning', message, title, link),
    errorNotification: (message, title, link) => notify('error', message, title, link),
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
